import { GenericTable } from '@/components/common/tables/common/generic-table/GenericTable';
import { Participant } from '@/pages/protected/Sales/components/services-sold-table/services-sold-tables.types';
import { parseDateToDMY } from '@/utils/utils';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import { differenceInYears } from 'date-fns';
import { ExternalLink } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

type TParticipantsInfo = {
  participants: Participant[];
};

export const ParticipantsInfo = ({ participants }: TParticipantsInfo) => {
  const { t } = useTranslation();
  const columns = useMemo<ColumnDef<Participant>[]>(
    () => [
      {
        accessorKey: 'firstName',
        header: t('sales.sections.participantsTable.firstName'),
        cell: ({ row }) => row?.original?.firstName || '-'
      },
      {
        accessorKey: 'lastName',
        header: t('sales.sections.participantsTable.lastName'),
        cell: ({ row }) => row?.original?.lastName || '-'
      },
      {
        accessorKey: 'isOwnUser',
        header: t('sales.sections.participantsTable.isOwnUser'),
        cell: ({ row }) => {
          const isCliente = row?.original?.isOwnUser;
          return <span>{isCliente ? t('common.yes') : t('common.no')}</span>;
        }
      },
      {
        accessorKey: 'birthdate',
        header: t('sales.sections.participantsTable.birthDate'),
        cell: ({ row }) => {
          if (!row?.original?.birthdate) {
            return <>-</>;
          }
          const birthdate = parseDateToDMY(row?.original?.birthdate);
          const age = differenceInYears(
            new Date(),
            new Date(row?.original?.birthdate)
          );
          return (
            <div className="flex items-center gap-2">
              <span>{birthdate}</span>
              <span className="text-xs text-gray-500">
                ({age} {t('sales.sections.participantsTable.yearsOld')})
              </span>
            </div>
          );
        }
      },
      {
        accessorKey: 'actions',
        header: () => (
          <div className="text-right">
            {t('sales.sections.participantsTable.moreInfo')}
          </div>
        ),
        cell: ({ row }) => {
          const userId = row.original.id;
          return (
            <Link
              to={`/users/${userId}`}
              target="_blank"
              className="flex items-center justify-end gap-2 underline cursor-pointer text-primary"
            >
              {t('sales.sections.participantsTable.seeMore')}
              <ExternalLink className="w-4 h-4" />
            </Link>
          );
        }
      }
    ],
    [t]
  );

  const table = useReactTable({
    data: participants,
    columns,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <div className="space-y-2">
      <GenericTable table={table} isLoading={false} />
    </div>
  );
};
